import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import I18n from 'i18n-js';
import sanitizeHtml from 'sanitize-html';
import {
  plato_api_notebook_path,
  plato_api_reading_game_path,
  plato_api_slide_show_path,
  plato_api_slide_group_path,
  plato_api_section_path,
} from 'modules/routes';

interface UseSectionTitleProps {
  enabled?: boolean;
  exerciseId: number;
  exerciseType: string;
}

// AIDEV-NOTE: exercise types absent here (Assessment, Challenge) have no section title — their
// cards show "Question N" instead. Keep the query disabled for them rather than requesting ''.
const PARENT_ATTRS_PATHS = {
  Notebook: plato_api_notebook_path,
  ReadingGame: plato_api_reading_game_path,
  Section: plato_api_section_path,
  SlideGroup: plato_api_slide_group_path,
  SlideShow: plato_api_slide_show_path,
};

const useSectionTitle = ({ enabled = true, exerciseId, exerciseType }: UseSectionTitleProps) => {
  const parentAttrsPath = PARENT_ATTRS_PATHS[exerciseType];
  const isEnabled = enabled && !!parentAttrsPath;

  const parentAttrsQuery = useQuery(
    ['parentAttrs', exerciseId, exerciseType],
    () => Axios.get(parentAttrsPath(exerciseId.toString())).then(res => res.data),
    { enabled: isEnabled },
  );

  const getSectionTitle = () => {
    if (!isEnabled) return '';
    if (parentAttrsQuery.isLoading) return 'Loading...';
    if (!parentAttrsQuery.data) return '';

    let modelName = '';
    let sectionHeaderAttrName;

    switch (exerciseType) {
      case 'Notebook':
        sectionHeaderAttrName = 'section_header';
        modelName = ` - ${I18n.t('notes')}`;
        break;
      case 'ReadingGame':
        sectionHeaderAttrName = 'section_header';
        modelName = ` - ${I18n.t('check')}`;
        break;
      case 'SlideGroup':
        sectionHeaderAttrName = 'sectionHeader';
        break;
      default:
        sectionHeaderAttrName = 'section_header';
    }
    const keys = Object.keys(parentAttrsQuery?.data);
    if (keys.length === 0) return '';

    const rawHeader = parentAttrsQuery.data[keys[0]]?.[sectionHeaderAttrName] || '';
    const sectionHeader = sanitizeHtml(rawHeader, { allowedTags: [] });
    return `${sectionHeader}${modelName}`;
  };

  return {
    // A disabled react-query v4 query still reports isLoading: true — don't leak that to callers
    // gating a skeleton on it.
    isLoading: isEnabled && parentAttrsQuery.isLoading,
    sectionTitle: getSectionTitle(),
  };
};

export default useSectionTitle;
